import { Controller } from "@hotwired/stimulus"

// Guided mode: step 0 is the Pflegegrad, steps 1–3 these services
const GUIDED_SERVICES = ["Pflegedienst", "Tagespflege", "Haushaltshilfe"]

export default class extends Controller {
  static values = { 
    budgetData: Object,
    pg: Number,
    isPublic: Boolean,
    usePflegedienst: String,
    useTagespflege: String,
    useHaushaltshilfe: String,
    guided: Boolean
  }

  static targets = [
    "pflegegradButton",
    "pgInfo",

    // Wizard step sections
    "stepPflegedienst",
    "stepTagespflege",
    "stepHaushaltshilfe",

    // Yes/No buttons
    "btnPflegedienstJa",
    "btnPflegedienstNein",
    "btnTagespflegeJa",
    "btnTagespflegeNein",
    "btnHaushaltshilfeJa",
    "btnHaushaltshilfeNein",

    // Expense panels (revealed on Ja)
    "pflegedienstExpense",
    "tagespflegeExpense",
    "haushaltshilfeExpense",

    // Inputs
    "expenseSachleistungen",
    "expenseHaushaltshilfe",
    "expenseTagespflege",

    // Range inputs
    "rangeSachleistungen",
    "rangeHaushaltshilfe",
    "rangeTagespflege",

    // Dynamic Labels
    "maxSachDisplay",
    "maxTagesDisplay",
    "maxHaushaltshilfeDisplay",

    // CTAs (service not used – shown on Nein)
    "ctaPflegedienstNein",
    "ctaTagespflegeNein",
    "ctaHaushaltshilfeNein",

    // Verhinderungspflege card
    "verhinderungspflegeCard",

    // Outputs
    "resultPflegegeld",
    "resultZweckgebunden",
    "resultEigenanteil",
    "eigenanteilSection",
    "eigenanteilTip",
    "percentageUsed",
    "progressBar",

    // Result detail box
    "resultDetail",

    // Detailed Breakdown
    "detailSach",
    "detailTages",
    "detailEntlastung",

    // Signup CTA
    "signupButton",
    "resultsColumn",
    "csrfToken",

    // Public page only (all optional): verdict sentence, Pflegegeld breakdown, phone sticky bar
    "verdict",
    "detailPflegegeldVoll",
    "detailKuerzung",
    "resultSummary",
    "stickyBar",
    "stickyPflegegeld",

    // Public page guided mode: one question at a time (data-state on each step)
    "guidedStep",
    "guidedSummary",
    "guidedDot",
    "guidedProgress",
    "guidedHint",
    "unusedList"
  ]

  connect() {
    this.selectedPflegegrad = null;
    this.formatter = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    this.currencyFormatter = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const startPg = (this.pgValue >= 1 && this.pgValue <= 5) ? this.pgValue : 2;
    this.selectPflegegradValue(startPg, false); // Don't reset on initial load

    // Initialize Wizard Steps based on saved values (now Strings: "true", "false", or "")
    if (this.usePflegedienstValue === "true" || this.usePflegedienstValue === "false") {
      this._handleAnswer("Pflegedienst", this.usePflegedienstValue === "true", false); 
    }

    if (this.useTagespflegeValue === "true" || this.useTagespflegeValue === "false") {
      this._handleAnswer("Tagespflege", this.useTagespflegeValue === "true", false);
    }

    if (this.useHaushaltshilfeValue === "true" || this.useHaushaltshilfeValue === "false") {
      this._handleAnswer("Haushaltshilfe", this.useHaushaltshilfeValue === "true", false);
    }

    this.updateBudgetsUI();
    this.calculate();
    this.observeResultSummary();
    if (this.guidedValue) this.startGuided();
  }

  disconnect() {
    if (this.saveTimeout) clearTimeout(this.saveTimeout);
    this.observer?.disconnect();
  }

  // Phone sticky bar (lg:hidden): visible once the result summary has been scrolled past,
  // not before — above it the bar would cover the Pflegegrad buttons
  observeResultSummary() {
    if (!this.hasStickyBarTarget || !this.hasResultSummaryTarget || !("IntersectionObserver" in window)) return;
    this.observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.stickyBarTarget.classList.toggle("translate-y-full", !show);
      this.stickyBarTarget.inert = !show;
    });
    this.observer.observe(this.resultSummaryTarget);
  }

  // Pflegegrad

  selectPflegegrad(event) {
    const pg = parseInt(event.currentTarget.dataset.value);
    if (!this.guidedValue) return this.selectPflegegradValue(pg, true); // User manually clicked, so reset

    // Guided: later answers stay, only the budgets change
    this.selectPflegegradValue(pg, false);
    this.pflegegradChosen = true;
    this.advanceGuided(0);
  }

  selectPflegegradValue(pg, reset = true) {
    this.selectedPflegegrad = pg;

    this.pflegegradButtonTargets.forEach(btn => {
      const isSelected = parseInt(btn.dataset.value) === pg;
      // Segmented buttons (aria-pressed) style themselves via aria-pressed: variants
      if (btn.hasAttribute("aria-pressed")) return btn.setAttribute("aria-pressed", isSelected);
      btn.classList.toggle("border-ph24_blue", isSelected);
      btn.classList.toggle("bg-ph24_blue/5", isSelected);
      btn.classList.toggle("ring-2", isSelected);
      btn.classList.toggle("ring-ph24_blue", isSelected);
      btn.classList.toggle("border-ph24_blue/20", !isSelected);
      btn.classList.toggle("hover:border-ph24_blue/40", !isSelected);
    });

    // Reveal step 2 (Pflegedienst question)
    this._show(this.stepPflegedienstTargets);

    if (reset) {
        // Reset downstream steps so re-selecting PG clears prior answers
        this.usePflegedienstValue = "";
        this.useTagespflegeValue = "";
        this.useHaushaltshilfeValue = "";
        this._resetStep("Pflegedienst");
        this._resetStep("Tagespflege");
        this._resetStep("Haushaltshilfe");
        this._hide(this.stepTagespflegeTargets);
        this._hide(this.stepHaushaltshilfeTargets);
    }
    
    this.updateBudgetsUI();
    this.calculate();
  }

  verhinderungspflegeCardTargetConnected() {
    this.updateVerhinderungspflegeVisibility();
  }

  answerPflegedienst(event) {
    const ja = event.currentTarget.dataset.answer === "ja";
    this.usePflegedienstValue = ja ? "true" : "false";
    this._handleAnswer("Pflegedienst", ja);
    this._guidedAnswered(1, ja);
  }

  answerTagespflege(event) {
    const ja = event.currentTarget.dataset.answer === "ja";
    this.useTagespflegeValue = ja ? "true" : "false";
    this._handleAnswer("Tagespflege", ja);
    this._guidedAnswered(2, ja);
  }

  answerHaushaltshilfe(event) {
    const ja = event.currentTarget.dataset.answer === "ja";
    this.useHaushaltshilfeValue = ja ? "true" : "false";
    this._handleAnswer("Haushaltshilfe", ja);
    this._guidedAnswered(3, ja);
  }

  _handleAnswer(name, ja, scroll = true) {
    const jaBtn   = this[`btn${name}JaTarget`];
    const neinBtn = this[`btn${name}NeinTarget`];
    this._styleYesNo(jaBtn, neinBtn, ja);

    const expenseTargets = this[`${name.toLowerCase()}ExpenseTargets`];
    const ctaNeinTargets = this[`cta${name}NeinTargets`];

    if (ja) {
      this._show(expenseTargets);
      this._hide(ctaNeinTargets);
    } else {
      this._hide(expenseTargets);
      this._show(ctaNeinTargets);
      
      // Zero out
      const input = this[`expense${name === 'Pflegedienst' ? 'Sachleistungen' : name}Target`];
      const range = this[`range${name === 'Pflegedienst' ? 'Sachleistungen' : name}Target`];
      if (input) input.value = 0;
      if (range) range.value = 0;
    }

    // Reveal next step
    let nextStepTargets = [];
    if (name === "Pflegedienst") nextStepTargets = this.stepTagespflegeTargets;
    if (name === "Tagespflege")  nextStepTargets = this.stepHaushaltshilfeTargets;

    if (nextStepTargets.length > 0) {
      this._show(nextStepTargets);
      if (scroll) this._scrollToNext(nextStepTargets[0]);
    } else if (name === "Haushaltshilfe" && scroll && this.hasResultsColumnTarget) {
      // Last wizard step answered — nothing left to reveal, so scroll to the
      // result instead (otherwise it stays below the fold on mobile). Only in the
      // step-by-step wizard; the public page shows all inputs at once.
      if (this.hasStepPflegedienstTarget) this._scrollToNext(this.resultsColumnTarget);
    }

    this.calculate();
  }

  // Guided mode (public page)

  // Starts on the Pflegegrad question; the result shows the server-rendered Pflegegrad 2
  // figures as an example until the first answer
  startGuided() {
    this.pflegegradChosen = false;
    this.currentStep = 0;
    this.pflegegradButtonTargets.forEach(btn => btn.setAttribute("aria-pressed", false));
    this.renderGuided();
  }

  nextStep(event) {
    this.advanceGuided(parseInt(event.currentTarget.dataset.stepIndex));
  }

  editStep(event) {
    this.currentStep = parseInt(event.currentTarget.dataset.stepIndex);
    this.renderGuided();
    this._focusGuided();
  }

  // Ja keeps the step open for the amount (Weiter button), Nein moves on
  _guidedAnswered(index, ja) {
    if (!this.guidedValue) return;
    ja ? this.renderGuided() : this.advanceGuided(index);
  }

  // Next unanswered step after `from`, or the finished result
  advanceGuided(from) {
    this.currentStep = [1, 2, 3].find(i => i > from && !this._isAnswered(i)) ?? 4;
    this.renderGuided();
    this._focusGuided();
  }

  renderGuided() {
    const answered = [0, 1, 2, 3].map(i => this._isAnswered(i));
    const count = answered.filter(Boolean).length;

    this.guidedStepTargets.forEach((step, i) => {
      step.dataset.state = i === this.currentStep ? "active" : (answered[i] ? "done" : "locked");
    });
    this.guidedSummaryTargets.forEach((el, i) => { el.textContent = this._stepSummary(i); });
    this.guidedDotTargets.forEach((dot, i) => dot.toggleAttribute("data-on", answered[i]));

    if (this.hasGuidedProgressTarget) {
      this.guidedProgressTarget.textContent = this.pflegegradChosen
        ? `Zwischenstand nach ${count} von 4 Fragen`
        : `Beispielrechnung für Pflegegrad ${this.selectedPflegegrad}`;
    }
    if (this.hasGuidedHintTarget) {
      this.guidedHintTarget.textContent = this.pflegegradChosen
        ? "Das Ergebnis wird mit jeder Antwort genauer."
        : "Wählen Sie zuerst den Pflegegrad. Danach rechnet das Ergebnis mit Ihren Angaben.";
    }
    if (this.hasResultsColumnTarget) {
      this.resultsColumnTarget.dataset.state = this.currentStep >= 4 ? "done" : (this.pflegegradChosen ? "progress" : "example");
    }
    if (this.hasUnusedListTarget) {
      this.unusedListTarget.classList.toggle("hidden", !GUIDED_SERVICES.some(name => this[`use${name}Value`] === "false"));
    }
  }

  _isAnswered(index) {
    if (index === 0) return this.pflegegradChosen;
    return this[`use${GUIDED_SERVICES[index - 1]}Value`] !== "";
  }

  _stepSummary(index) {
    if (index === 0) return this.pflegegradChosen ? `Pflegegrad ${this.selectedPflegegrad}` : "";

    const name = GUIDED_SERVICES[index - 1];
    const use = this[`use${name}Value`];
    if (use === "false") return "Nein";
    if (use !== "true") return "";

    const input = this[`expense${name === "Pflegedienst" ? "Sachleistungen" : name}Target`];
    return `Ja, ${this.currencyFormatter.format(parseFloat(input.value) || 0)} im Monat`;
  }

  // Focus follows the flow (the clicked button just got hidden); phones scroll along
  _focusGuided() {
    const done = this.currentStep >= 4;
    const target = done ? this.resultsColumnTarget : this.guidedStepTargets[this.currentStep];
    if (!target) return;

    const focusable = done ? target : target.querySelector('[aria-pressed="true"]') || target.querySelector("[aria-pressed]");
    focusable?.focus({ preventScroll: true });
    this._scrollToNext(target);
  }

  // Range / Input sync 

  updateFromRange(event) {
    const inputId = event.currentTarget.dataset.syncInput;
    const val = event.currentTarget.value;
    const inputTarget = this.targets.find(inputId);
    if (inputTarget) inputTarget.value = val;
    this.calculate();
  }

  updateFromInput(event) {
    const rangeId = event.currentTarget.dataset.syncRange;
    let val = parseInt(event.currentTarget.value) || 0;
    const rangeTarget = this.targets.find(rangeId);

    if (rangeTarget) {
      const max = parseInt(rangeTarget.max);
      rangeTarget.value = Math.min(Math.max(val, 0), max);
    }

    this.calculate();
  }

  // Budget UI update

  updateBudgetsUI() {
    const data = this.budgetDataValue[this.selectedPflegegrad];
    if (!data) return;

    this.updateVerhinderungspflegeVisibility();

    const entlastungsbetrag = this.getAmount(data, "Entlastungsbetrag") || 131;
    const maxSach = this.getAmount(data, "Pflegesachleistungen");
    const sliderMaxSach = Math.max(maxSach, entlastungsbetrag);

    if (this.hasMaxSachDisplayTarget) {
      this.maxSachDisplayTarget.textContent = maxSach > 0
        ? `Sachleistungen-Budget: ${this.currencyFormatter.format(maxSach)}`
        : `Maximal nutzbar: ${this.currencyFormatter.format(entlastungsbetrag)} (Entlastungsbetrag)`;
    }

    if (this.hasRangeSachleistungenTarget) this.rangeSachleistungenTarget.max = sliderMaxSach;

    // Haushaltshilfe max: Entlastungsbetrag + 40% of Sachleistungen (2026 rule)
    const maxHH = entlastungsbetrag + maxSach * 0.4;
    if (this.hasMaxHaushaltshilfeDisplayTarget) {
      this.maxHaushaltshilfeDisplayTarget.textContent =
        `Max. Kassenbudget: ${this.currencyFormatter.format(maxHH)} (Entlastungsbetrag + 40% Sachleistungen)`;
    }
    if (this.hasRangeHaushaltshilfeTarget) this.rangeHaushaltshilfeTarget.max = maxHH;

    const maxTages = this.getAmount(data, "Tages- und Nachtpflege");
    const sliderMaxTages = Math.max(maxTages, entlastungsbetrag);

    if (this.hasMaxTagesDisplayTarget) {
      this.maxTagesDisplayTarget.textContent = maxTages > 0
        ? `Tagespflege-Budget: ${this.currencyFormatter.format(maxTages)}`
        : `Maximal nutzbar: ${this.currencyFormatter.format(entlastungsbetrag)} (Entlastungsbetrag)`;
    }

    if (this.hasRangeTagespflegeTarget) this.rangeTagespflegeTarget.max = sliderMaxTages;

    const maxPflegegeld = this.getAmount(data, "Pflegegeld");

    if (this.hasResultDetailTarget) {
      if (this.selectedPflegegrad === 1) {
        this.resultDetailTarget.innerHTML = 'Bei Pflegegrad 1 gibt es kein Pflegegeld. Nutzen Sie den Entlastungsbetrag von 131 €/Monat.';
      } else {
        this.resultDetailTarget.innerHTML = `Bei Pflegegrad ${this.selectedPflegegrad} beträgt das volle Pflegegeld <span class="font-semibold">${this.currencyFormatter.format(maxPflegegeld)}</span>.`;
      }
      this.resultDetailTarget.classList.remove('hidden');
    }
  }

  // Calculation

  calculate() {
    if (!this.selectedPflegegrad) return;

    // Show/Hide results based on wizard progress
    if (this.hasResultsColumnTarget) {
      this.resultsColumnTarget.classList.toggle("hidden", !this.selectedPflegegrad);
    }

    const data = this.budgetDataValue[this.selectedPflegegrad];
    if (!data) return;

    const maxPflegegeld     = this.getAmount(data, "Pflegegeld");
    const maxSachleistungen = this.getAmount(data, "Pflegesachleistungen");
    const maxTagespflege    = this.getAmount(data, "Tages- und Nachtpflege");
    const entlastungsbetrag = this.getAmount(data, "Entlastungsbetrag") || 131;

    const inputPflegedienst  = this.hasExpenseSachleistungenTarget ? parseFloat(this.expenseSachleistungenTarget.value) || 0 : 0;
    const inputHaushaltshilfe = this.hasExpenseHaushaltshilfeTarget ? parseFloat(this.expenseHaushaltshilfeTarget.value) || 0 : 0;
    const inputTagespflege   = this.hasExpenseTagespflegeTarget ? parseFloat(this.expenseTagespflegeTarget.value) || 0 : 0;

    // 1. Haushaltshilfe draws on the Entlastungsbetrag first (§ 45b SGB XI); only the
    //    rest comes from the Umwandlungsanspruch, max. 40% of the Sachleistungen (§ 45a Abs. 4)
    const hhFromEntlastung = Math.min(inputHaushaltshilfe, entlastungsbetrag);
    const hhRest           = inputHaushaltshilfe - hhFromEntlastung;

    // 2. Sachleistungen: Pflegedienst first, then the converted Haushaltshilfe share
    const dienstFromSach = Math.min(inputPflegedienst, maxSachleistungen);
    const hhFromSach     = Math.min(hhRest, maxSachleistungen * 0.4, maxSachleistungen - dienstFromSach);
    const coveredBySach  = dienstFromSach + hhFromSach;
    const overflowSach   = (inputPflegedienst - dienstFromSach) + (hhRest - hhFromSach);

    // 3. Pflegegeld reduction (Kombinationsleistung)
    let pctUsed = maxSachleistungen > 0 ? coveredBySach / maxSachleistungen : 0;
    pctUsed = Math.min(1, Math.max(0, pctUsed));
    const remainingPflegegeld = maxPflegegeld * (1 - pctUsed);

    // 4. Tagespflege
    const coveredByTages = Math.min(inputTagespflege, maxTagespflege);
    const overflowTages  = inputTagespflege - coveredByTages;

    // 5. Whatever is left of the Entlastungsbetrag covers remaining overflows
    const totalOverflow       = overflowSach + overflowTages;
    const coveredByEntlastung = Math.min(totalOverflow, entlastungsbetrag - hhFromEntlastung);
    const finalEigenanteil    = totalOverflow - coveredByEntlastung;

    // 6. Remaining pools
    const remainingSach       = maxSachleistungen - coveredBySach;
    const remainingTages      = maxTagespflege - coveredByTages;
    const remainingEntlastung = entlastungsbetrag - hhFromEntlastung - coveredByEntlastung;
    const totalZweckgebunden  = remainingSach + remainingTages + remainingEntlastung;

    // Output
    if (this.hasResultPflegegeldTarget)  this.resultPflegegeldTarget.textContent = `${this.formatter.format(remainingPflegegeld)} €`;
    if (this.hasResultZweckgebundenTarget) this.resultZweckgebundenTarget.textContent = this.currencyFormatter.format(totalZweckgebunden);

    if (this.hasEigenanteilSectionTarget) {
      this.eigenanteilSectionTarget.classList.toggle('hidden', finalEigenanteil <= 0);
      if (this.hasResultEigenanteilTarget) this.resultEigenanteilTarget.textContent = this.currencyFormatter.format(finalEigenanteil);
    }

    // Verhinderungspflege tip only from PG 2
    if (this.hasEigenanteilTipTarget) this.eigenanteilTipTarget.classList.toggle('hidden', this.selectedPflegegrad < 2);

    if (this.hasDetailSachTarget)      this.detailSachTarget.textContent = this.currencyFormatter.format(remainingSach);
    if (this.hasDetailTagesTarget)     this.detailTagesTarget.textContent = this.currencyFormatter.format(remainingTages);
    if (this.hasDetailEntlastungTarget) this.detailEntlastungTarget.textContent = this.currencyFormatter.format(remainingEntlastung);

    const sachPct = Math.round(pctUsed * 100);
    if (this.hasPercentageUsedTarget) this.percentageUsedTarget.textContent = `${sachPct}%`;
    if (this.hasProgressBarTarget)    this.progressBarTarget.style.width = `${sachPct}%`;

    this.renderBreakdown(maxPflegegeld, remainingPflegegeld, pctUsed, entlastungsbetrag);

    if (this.hasSignupButtonTarget) {
      const params = new URLSearchParams({ 
        pg: this.selectedPflegegrad, 
        sach: inputPflegedienst, 
        haushalt: inputHaushaltshilfe, 
        tages: inputTagespflege,
        use_sach: this.usePflegedienstValue,
        use_tages: this.useTagespflegeValue,
        use_hh: this.useHaushaltshilfeValue
      });
      this.signupButtonTargets.forEach(btn => { btn.href = `/pflegebudget_signup?${params.toString()}`; });
    }

    if (!this.isPublicValue) {
      if (this.saveTimeout) clearTimeout(this.saveTimeout);
      this.saveTimeout = setTimeout(() => this.saveToBackend(inputPflegedienst, inputHaushaltshilfe, inputTagespflege), 1000);
    }
  }

  // Public page: verdict sentence, Pflegegeld breakdown rows and sticky bar figure
  renderBreakdown(maxPflegegeld, remainingPflegegeld, pctUsed, entlastungsbetrag) {
    const eur = value => this.currencyFormatter.format(value);
    const pct = Math.round(pctUsed * 100);

    if (this.hasDetailPflegegeldVollTarget) this.detailPflegegeldVollTarget.textContent = eur(maxPflegegeld);
    if (this.hasDetailKuerzungTarget) this.detailKuerzungTarget.textContent = eur(maxPflegegeld - remainingPflegegeld);
    if (this.hasStickyPflegegeldTarget) this.stickyPflegegeldTarget.textContent = eur(remainingPflegegeld);
    if (!this.hasVerdictTarget) return;

    let text;
    if (this.selectedPflegegrad === 1) {
      text = `Bei Pflegegrad 1 gibt es kein Pflegegeld. Zweckgebunden stehen Ihnen ${eur(entlastungsbetrag)} Entlastungsbetrag im Monat zu.`;
    } else if (pctUsed === 0) {
      text = `In dieser Situation bei Pflegegrad ${this.selectedPflegegrad} zahlt die Pflegekasse das volle Pflegegeld von ${eur(maxPflegegeld)} im Monat aus.`;
    } else if (pctUsed >= 1) {
      text = "Die Pflegesachleistung ist voll ausgeschöpft, Pflegegeld wird nicht mehr ausgezahlt.";
    } else {
      text = `Sie nutzen ${pct} % der Pflegesachleistung, daher kommen ${100 - pct} % des Pflegegelds an: ${eur(remainingPflegegeld)} statt ${eur(maxPflegegeld)}.`;
    }
    this.verdictTarget.textContent = text;
  }

  updateVerhinderungspflegeVisibility() {
    if (this.hasVerhinderungspflegeCardTarget) {
      this.verhinderungspflegeCardTarget.classList.toggle("hidden", this.selectedPflegegrad < 2);
    }
  }

  // Backend persistence 
  async saveToBackend(sach, hh, tages) {
    if (!this.hasCsrfTokenTarget) {
      console.warn("CSRF token target missing");
      return;
    }

    const csrfToken = this.csrfTokenTarget.content;
    const body = new FormData();
    body.append("user_pflegebudget[expense_sachleistungen]", sach);
    body.append("user_pflegebudget[expense_haushaltshilfe]", hh);
    body.append("user_pflegebudget[expense_tagespflege]", tages);
    body.append("user_pflegebudget[use_pflegedienst]", this.usePflegedienstValue);
    body.append("user_pflegebudget[use_tagespflege]", this.useTagespflegeValue);
    body.append("user_pflegebudget[use_haushaltshilfe]", this.useHaushaltshilfeValue);

    try {
      const response = await fetch("/user_portal/pflegebudget", {
        method: 'PATCH',
        headers: { 
          "X-CSRF-Token": csrfToken, 
          "Accept": "application/json" 
        },
        body: body
      });

      if (!response.ok) {
        let errorMsg = `Server error: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.errors) {
            errorMsg = `Validation errors: ${JSON.stringify(errorData.errors)}`;
          }
        } catch (e) {
          // Response was not JSON
        }
        console.error(errorMsg);
        return;
      }

      if (response.status === 204) return;
      const data = await response.json();
      if (data.success === false) {
        console.error("Save failed:", data.errors || "Unknown error");
      }
    } catch (error) {
      console.error("Network error saving budget:", error);
    }
  }

  getAmount(data, key) {
    return (data[key] && data[key].amount) ? data[key].amount : 0;
  }

  _show(elements) {
    [].concat(elements).forEach(el => el && el.classList.remove("hidden"));
  }

  _hide(elements) {
    [].concat(elements).forEach(el => el && el.classList.add("hidden"));
  }

  /** Style selected / unselected Yes-No buttons */
  _styleYesNo(jaBtn, neinBtn, jaSelected) {
    if (jaBtn?.hasAttribute("aria-pressed") && neinBtn) {
      jaBtn.setAttribute("aria-pressed", jaSelected);
      neinBtn.setAttribute("aria-pressed", !jaSelected);
      return;
    }
    const activeClasses   = ["border-ph24_blue", "bg-ph24_blue/5", "ring-2", "ring-ph24_blue"];
    const inactiveClasses = ["border-ph24_blue/20"];

    const activate   = btn => { btn.classList.add(...activeClasses);    btn.classList.remove(...inactiveClasses); };
    const deactivate = btn => { btn.classList.remove(...activeClasses); btn.classList.add(...inactiveClasses); };

    if (jaBtn && neinBtn) {
      jaSelected ? activate(jaBtn) : deactivate(jaBtn);
      jaSelected ? deactivate(neinBtn) : activate(neinBtn);
    }
  }

  /** Reset a step's Yes/No state and hide its sub-panels. */
  _resetStep(name) {
    const jaBtn    = this[`hasBtn${name}JaTarget`]    ? this[`btn${name}JaTarget`]    : null;
    const neinBtn  = this[`hasBtn${name}NeinTarget`]  ? this[`btn${name}NeinTarget`]  : null;
    const expense  = this[`${name.toLowerCase()}ExpenseTargets`];
    const ctaNein  = this[`cta${name}NeinTargets`];

    const reset = ["border-ph24_blue", "bg-ph24_blue/5", "ring-2", "ring-ph24_blue"];
    const idle  = ["border-ph24_blue/20"];
    [jaBtn, neinBtn].forEach(btn => {
      if (!btn) return;
      if (btn.hasAttribute("aria-pressed")) return btn.setAttribute("aria-pressed", false);
      btn.classList.remove(...reset);
      btn.classList.add(...idle);
    });

    this._hide(expense);
    this._hide(ctaNein);

    const suffix = name === "Pflegedienst" ? "Sachleistungen" : name;
    const input = this[`hasExpense${suffix}Target`] ? this[`expense${suffix}Target`] : null;
    const range = this[`hasRange${suffix}Target`] ? this[`range${suffix}Target`] : null;
    if (input) input.value = 0;
    if (range) range.value = 0;
  }

  /** Smoothly scroll to the next step on mobile */
  _scrollToNext(el) {
    if (!el) return;
    setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
  }
}